import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import { api, download } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth, isStaff } from "../lib/auth";
import { useMaster, nameOf } from "../lib/master";
import { fmtDateTime, kg, todayCL } from "../lib/time";
import { StatusBadge, ClassBadge } from "../components/Badges";
import { PageTitle } from "../components/Common";

export const ScopeFilters = ({ q, setQ, withStatus, noDate }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const growers = useMaster("growers");
  const farms = useMaster("farms").filter((f) => !q.grower_id || f.grower_id === q.grower_id);
  const set = (k) => (e) => setQ({ ...q, [k]: e.target.value });
  if (noDate && !withStatus && !isStaff(user) && farms.length <= 1) return null;
  return (
    <div className="card p-3 flex flex-wrap gap-3 items-end mb-4" data-testid="scope-filters">
      {!noDate && <label><span className="eyebrow">{t("date")}</span><input type="date" className="field mt-1 !min-h-[44px]" value={q.date} onChange={set("date")} data-testid="filter-date" /></label>}
      {isStaff(user) && <label><span className="eyebrow">{t("grower")}</span>
        <select className="field mt-1 !min-h-[44px]" value={q.grower_id} onChange={(e) => setQ({ ...q, grower_id: e.target.value, farm_id: "" })} data-testid="filter-grower">
          <option value="">{t("all_growers")}</option>{growers.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</select></label>}
      {farms.length > 1 && <label><span className="eyebrow">{t("farm")}</span>
        <select className="field mt-1 !min-h-[44px]" value={q.farm_id} onChange={set("farm_id")} data-testid="filter-farm">
          <option value="">{t("all_farms")}</option>{farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}</select></label>}
      {withStatus && <label><span className="eyebrow">{t("status")}</span>
        <select className="field mt-1 !min-h-[44px]" value={q.status} onChange={set("status")} data-testid="filter-status">
          <option value="">—</option>{["active", "ready", "sent", "received", "verified", "dispatched", "discrepancy", "closed"].map((s) => <option key={s} value={s}>{t(`status_${s}`)}</option>)}</select></label>}
    </div>
  );
};

const clean = (q) => Object.fromEntries(Object.entries(q).filter(([, v]) => v));

export default function Bins() {
  const { t, lang } = useLang();
  const [q, setQ] = useState({ date: todayCL(), grower_id: "", farm_id: "", status: "" });
  const [rows, setRows] = useState([]);
  const sectors = useMaster("sectors");
  const contractors = useMaster("contractors");
  useEffect(() => { api.get("/bins", { params: clean(q) }).then((r) => setRows(r.data)).catch(() => {}); }, [q]);

  return (
    <div data-testid="bins-page">
      <PageTitle right={<button className="btn-secondary btn-sm" onClick={() => download("/bins/export.csv", `bins-${q.date || "all"}.csv`, { params: clean(q) })} data-testid="export-csv-button"><Download size={16} />{t("export_csv")}</button>}>
        {t("nav_bins")} <span className="text-slate-400 font-mono text-xl">({rows.length})</span>
      </PageTitle>
      <ScopeFilters q={q} setQ={setQ} withStatus />
      <div className="card overflow-x-auto">
        <table className="tbl w-full" data-testid="bins-table">
          <thead><tr><th>{t("label")}</th><th>{t("status")}</th><th>{t("sector")}</th><th>{t("contractor")}</th><th>{t("activated_by")}</th><th>{t("field_weight")}</th><th>{t("net")}</th></tr></thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.id} className="hover:bg-slate-50">
                <td><Link to={`/bin/${b.id}`} className="font-mono font-bold text-[#800B28] underline" data-testid={`bins-table-link-${b.label_number}`}>{b.label_number}</Link></td>
                <td><StatusBadge status={b.status} /></td>
                <td>{nameOf(sectors, b.sector_id)}</td>
                <td>{nameOf(contractors, b.contractor_id)}</td>
                <td className="text-xs">{b.activated_by_name}<br />{fmtDateTime(b.activated_at, lang)}</td>
                <td>{b.field_weight ? kg(b.field_weight.kg) : "—"}</td>
                <td className="space-y-1">{b.dock ? <><b className="font-mono">{kg(b.dock.net_kg)}</b><br /><ClassBadge value={b.dock.classification} /></> : "—"}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} className="text-slate-500">{t("none")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
